import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton, Modal, Button, Divider } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import TestingImg from "../../common/assests/gridimg3.png";
import Paginations from "../../common/pagination/Paginations";
import { useSelector } from "react-redux";
import {
  allVenderEventsApi,
  blockVenderEventApi,
  deleteVenderEventApi,
  resumeVenderEventApi,
} from "../../api";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useConfirm } from "../../common/confirmDialog/useConfirm";
import configuration from "../../config/Config";
import { AdminTable, ActionCell } from "../../common/adminTable";
import StatusChip from "../../common/statusChip/StatusChip";

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

function Field({ label, value, icon }) {
  return (
    <Box>
      <Typography
        sx={{
          fontFamily: "Heebo",
          fontSize: 10,
          color: "rgba(0,0,0,0.4)",
          mb: 0.25,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {icon &&
          React.cloneElement(icon, {
            sx: { fontSize: 14, color: "#065F46", flexShrink: 0, ...icon.props?.sx },
          })}
        <Typography sx={{ fontFamily: "Heebo", fontSize: 13, fontWeight: 500, color: "#362B24" }}>
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  );
}

const CreatedEvent = () => {
  const token = useSelector((state) => state?.user?.user?.token);
  const confirm = useConfirm();
  const { id } = useParams();
  const [eventData, setEventData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState();
  const [change, setChange] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState();

  const handleAllEventsData = async () => {
    try {
      setLoading(true);
      const response = await allVenderEventsApi({ token, currentPage, id });
      setEventData(response?.events || []);
      setCurrentPage(response?.currentPage);
      setTotalPages(response?.totalPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("error", error);
    }
  };

  const handleBlock = async (id) => {
    if (!await confirm({ title: "Unpublish Event", description: "Are you sure you want to unpublish this event?", confirmText: "Unpublish" })) return;
    try {
      const response = await blockVenderEventApi({ token, id });
      setChange(!change);
      toast.success(response.message);
      setOpen(false);
    } catch (error) {
      toast.error(error?.status === 500 ? "Some error occured" : error?.error);
      console.log("error", error);
    }
  };

  const handleResume = async (id) => {
    if (!await confirm({ title: "Publish Event", description: "Are you sure you want to publish this event?", confirmText: "Publish" })) return;
    try {
      const response = await resumeVenderEventApi({ token, id });
      setChange(!change);
      toast.success(response.message);
      setOpen(false);
    } catch (error) {
      toast.error(error?.status === 500 ? "Some error occured" : error?.error);
      console.log("error", error);
    }
  };

  const handleDelete = async (id) => {
    if (!await confirm({ title: "Delete Event", description: "Are you sure you want to delete this event?", confirmText: "Delete", destructive: true })) return;
    try {
      const response = await deleteVenderEventApi({ token, id });
      setChange(!change);
      toast.success(response.message);
      setOpen(false);
    } catch (error) {
      toast.error(error?.status === 500 ? "Some error occured" : error?.error);
      console.log("error", error);
    }
  };

  useEffect(() => {
    handleAllEventsData();
  }, [currentPage, change]);

  const handleOpen = (event) => {
    setSelectedEvent(event);
    setOpen(true);
  };

  const isPublished = selectedEvent?.status === "active";
  const handleClose = () => setOpen(false);

  const handlePageChange = (page) => {
    setLoading(true);
    setCurrentPage(page);
  };

  const columns = [
    {
      field: "picture",
      headerName: "Image",
      width: 70,
      renderCell: (row) => (
        <img
          src={row?.picture ? `${configuration.cdnFront}/${row.picture}` : TestingImg}
          alt="event"
          style={{ height: 40, width: 40, borderRadius: "6px", objectFit: "cover" }}
        />
      ),
    },
    { field: "title", headerName: "Title", minWidth: 140, renderCell: (r) => r?.title || "-" },
    {
      field: "eventDate",
      headerName: "Event Date",
      width: 120,
      cellSx: { whiteSpace: "nowrap" },
      renderCell: (r) => fmtDate(r?.eventDate),
    },
    { field: "cTag", headerName: "Category", width: 120, renderCell: (r) => r?.cTag || "-" },
    {
      field: "description",
      headerName: "Description",
      minWidth: 180,
      cellSx: { maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
      renderCell: (r) => r?.description || "-",
    },
    { field: "status", headerName: "Status", width: 90, renderCell: (r) => <StatusChip value={r?.status} /> },
    {
      field: "__actions",
      headerName: "",
      width: 80,
      align: "right",
      renderCell: (row) => (
        <ActionCell
          actions={[
            {
              label: "View",
              icon: <VisibilityIcon fontSize="small" />,
              color: "#065F46",
              onClick: () => handleOpen(row),
            },
            {
              label: "Delete",
              icon: <DeleteOutlineIcon fontSize="small" />,
              color: "#EA001B",
              onClick: () => handleDelete(row?._id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Box sx={{ mt: "40px", mb: "30px" }}>
      <Typography sx={{ fontSize: "20px", fontWeight: "500", color: "#362B24", fontFamily: "Heebo", mb: 2 }}>
        All Created Events
      </Typography>
      <AdminTable
        columns={columns}
        rows={eventData}
        loading={loading}
        rowKey="_id"
        emptyText="No events found"
      />
      <Paginations
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        disabled={loading}
      />

      {/* Event Detail Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92vw", sm: "560px" },
            maxWidth: "560px",
            bgcolor: "#fff",
            borderRadius: "20px",
            boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
            display: "flex",
            flexDirection: "column",
            maxHeight: "88vh",
            overflow: "hidden",
          }}
        >
          {/* Fixed header */}
          <Box
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #F0F0F0",
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                sx={{ fontFamily: "Heebo", fontWeight: 700, fontSize: 16, color: "#1a1a1a" }}
              >
                Event Details
              </Typography>
              {selectedEvent && <StatusChip value={selectedEvent?.status} />}
            </Box>
            <IconButton size="small" onClick={handleClose} sx={{ color: "rgba(0,0,0,0.4)" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Scrollable body */}
          <Box
            sx={{
              overflowY: "auto",
              px: 3,
              py: 2.5,
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {selectedEvent && (
              <>
                <Box
                  component="img"
                  src={
                    selectedEvent?.picture
                      ? `${configuration.cdnFront}/${selectedEvent?.picture}`
                      : TestingImg
                  }
                  alt="Event"
                  sx={{
                    width: "100%",
                    height: 200,
                    objectFit: "cover",
                    borderRadius: "12px",
                    mb: 2.5,
                    display: "block",
                  }}
                />

                <Typography
                  sx={{
                    fontFamily: "Heebo",
                    fontWeight: 700,
                    fontSize: 18,
                    color: "#1a1a1a",
                    mb: 2,
                  }}
                >
                  {selectedEvent?.title || "—"}
                </Typography>

                <Box
                  sx={{
                    bgcolor: "#F8F9FA",
                    borderRadius: "12px",
                    p: 2,
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                    mb: 2.5,
                  }}
                >
                  <Field
                    label="Date"
                    value={fmtDate(selectedEvent?.eventDate)}
                    icon={<CalendarTodayOutlinedIcon />}
                  />
                  <Field
                    label="Category"
                    value={selectedEvent?.cTag || selectedEvent?.category}
                    icon={<LabelOutlinedIcon />}
                  />
                  <Field
                    label="Location"
                    value={selectedEvent?.address}
                    icon={<LocationOnOutlinedIcon />}
                  />
                </Box>

                {selectedEvent?.description && (
                  <>
                    <Typography
                      sx={{
                        fontFamily: "Heebo",
                        fontWeight: 600,
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: 0.7,
                        color: "rgba(0,0,0,0.4)",
                        mb: 0.75,
                      }}
                    >
                      Description
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Heebo",
                        fontSize: 13,
                        color: "#362B24",
                        lineHeight: 1.7,
                        mb: 2.5,
                      }}
                    >
                      {selectedEvent?.description}
                    </Typography>
                  </>
                )}

                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="text"
                    onClick={
                      isPublished
                        ? () => handleBlock(selectedEvent?._id)
                        : () => handleResume(selectedEvent?._id)
                    }
                    startIcon={isPublished ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                    sx={{
                      flex: 1,
                      borderRadius: "10px",
                      fontFamily: "Heebo",
                      textTransform: "none",
                      fontSize: 13,
                      fontWeight: 500,
                      color: isPublished ? "#B45309" : "#065F46",
                      "&:hover": { bgcolor: isPublished ? "#FFFBEB" : "#ECFDF5" },
                    }}
                  >
                    {isPublished ? "Unpublish" : "Publish"}
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => handleDelete(selectedEvent?._id)}
                    startIcon={<DeleteOutlineIcon fontSize="small" />}
                    sx={{
                      flex: 1,
                      borderRadius: "10px",
                      fontFamily: "Heebo",
                      textTransform: "none",
                      fontSize: 13,
                      fontWeight: 500,
                      color: "#EA001B",
                      "&:hover": { bgcolor: "#FEF2F2" },
                    }}
                  >
                    Delete
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default CreatedEvent;
